// frontend/src/components/projects/BulkPipelineWizard.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X, Loader2, AlertTriangle, CheckCircle2, ChevronRight,
  ChevronLeft, Database, ArrowRight, Search, Plus, Trash2, Zap,
  Sparkles, Info, EyeOff, Eye, Minus,
} from 'lucide-react';
import { useProjectStore } from '../../store/useProjectStore';
import { useLanguageStore } from '../../store/useLanguageStore';

// ══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════

const STEPS = ['reference', 'pairs', 'preview', 'confirm', 'success'];

const CONFIDENCE_COLORS = {
  high: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  medium: 'text-amber-700   bg-amber-50   border-amber-200',
  low: 'text-red-700     bg-red-50     border-red-200',
};

const getConfidenceLevel = (avg) => {
  if (avg >= 75) return 'high';
  if (avg >= 50) return 'medium';
  return 'low';
};

// ══════════════════════════════════════════════════════════════════════
// STEP 1: SELECT REFERENCE PIPELINE
// ══════════════════════════════════════════════════════════════════════

const StepReference = ({ pipelines, selected, onSelect, t }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return pipelines;
    const q = search.toLowerCase();
    return pipelines.filter(p =>
      (p.name || '').toLowerCase().includes(q)
    );
  }, [pipelines, search]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        {t('projects.bulk.step_reference_desc') || 'Selecciona un pipeline existente como referencia. Sus credenciales de conexión se usarán para todos los pipelines nuevos.'}
      </p>

      {pipelines.length > 5 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('common.search') || 'Buscar...'}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      )}

      <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">
            {t('projects.bulk.no_pipelines') || 'No hay pipelines disponibles en este proyecto.'}
          </p>
        ) : filtered.map(p => {
          const srcType = p.sourceType || '?';
          const tgtType = p.targetType || '?';
          const isSelected = selected === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all
                ${isSelected
                  ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-sm font-semibold ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                  {p.name}
                </span>
                {isSelected && <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />}
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-100 rounded text-slate-500 uppercase">
                  {srcType}
                </span>
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-100 rounded text-slate-500 uppercase">
                  {tgtType}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// STEP 2: SELECT TABLE PAIRS
// ══════════════════════════════════════════════════════════════════════

const StepPairs = ({
  sourceTables, targetTables, pairs, setPairs,
  sourceEngine, targetEngine, isLoading, t,
}) => {
  const [srcSearch, setSrcSearch] = useState('');
  const [tgtSearch, setTgtSearch] = useState('');
  const [syncMode, setSyncMode] = useState('upsert');

  const usedSources = useMemo(() => new Set(pairs.map(p => p.sourceTable)), [pairs]);
  const usedTargets = useMemo(() => new Set(pairs.map(p => p.targetTable)), [pairs]);

  const filteredSrc = useMemo(() => {
    const q = srcSearch.toLowerCase();
    return sourceTables.filter(t =>
      (!q || t.toLowerCase().includes(q)) && !usedSources.has(t)
    );
  }, [sourceTables, srcSearch, usedSources]);

  const filteredTgt = useMemo(() => {
    const q = tgtSearch.toLowerCase();
    return targetTables.filter(t =>
      (!q || t.toLowerCase().includes(q)) && !usedTargets.has(t)
    );
  }, [targetTables, tgtSearch, usedTargets]);

  const handleAutoMatch = useCallback(() => {
    const newPairs = [];
    const tgtLower = targetTables.map(t => ({ name: t, lower: t.toLowerCase() }));
    const existingTargets = new Set(pairs.map(p => p.targetTable));
    const existingSources = new Set(pairs.map(p => p.sourceTable));

    for (const src of sourceTables) {
      if (existingSources.has(src)) continue;
      const srcLow = src.toLowerCase();
      // Exact match first
      const exact = tgtLower.find(t => t.lower === srcLow && !existingTargets.has(t.name));
      if (exact) {
        newPairs.push({ sourceTable: src, targetTable: exact.name });
        existingTargets.add(exact.name);
        existingSources.add(src);
        continue;
      }
      // Contains match
      const contains = tgtLower.find(t =>
        (t.lower.includes(srcLow) || srcLow.includes(t.lower))
        && !existingTargets.has(t.name)
      );
      if (contains) {
        newPairs.push({ sourceTable: src, targetTable: contains.name });
        existingTargets.add(contains.name);
        existingSources.add(src);
      }
    }

    if (newPairs.length > 0) {
      setPairs(prev => [...prev, ...newPairs]);
    }
  }, [sourceTables, targetTables, pairs, setPairs]);

  const addPair = (src, tgt) => {
    setPairs(prev => [...prev, { sourceTable: src, targetTable: tgt }]);
  };

  const removePair = (idx) => {
    setPairs(prev => prev.filter((_, i) => i !== idx));
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500 mt-3">
          {t('projects.bulk.connecting') || 'Conectando a los sistemas...'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Auto-match button */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {t('projects.bulk.step_pairs_desc') || 'Selecciona las tablas de origen y destino para crear los pares.'}
        </p>
        <button
          onClick={handleAutoMatch}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {t('projects.bulk.auto_match') || 'Auto-emparejar'}
        </button>
      </div>

      {/* Table selector — two columns */}
      <div className="grid grid-cols-2 gap-3">
        {/* Source */}
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
            {t('wizard.step_source.title') || 'Origen'} ({sourceEngine})
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text" value={srcSearch} onChange={e => setSrcSearch(e.target.value)}
              placeholder={t('common.search') || 'Buscar...'}
              className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-200 rounded-lg p-2 bg-slate-50">
            {filteredSrc.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-3">
                {srcSearch ? (t('common.no_results') || 'Sin resultados') : (t('projects.bulk.all_paired') || 'Todas emparejadas')}
              </p>
            ) : filteredSrc.map(s => (
              <button
                key={s}
                onClick={() => {
                  // Find a matching target by name similarity or pick first available
                  const tgtLow = s.toLowerCase();
                  const match = filteredTgt.find(t => t.toLowerCase() === tgtLow)
                    || filteredTgt.find(t => t.toLowerCase().includes(tgtLow) || tgtLow.includes(t.toLowerCase()));
                  if (match) {
                    addPair(s, match);
                  }
                }}
                disabled={filteredTgt.length === 0}
                className="w-full text-left text-xs px-2.5 py-1.5 rounded-md hover:bg-indigo-50 hover:text-indigo-700 text-slate-700 transition truncate disabled:opacity-40"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Target */}
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
            {t('wizard.step_target.title') || 'Destino'} ({targetEngine})
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              type="text" value={tgtSearch} onChange={e => setTgtSearch(e.target.value)}
              placeholder={t('common.search') || 'Buscar...'}
              className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-200 rounded-lg p-2 bg-slate-50">
            {filteredTgt.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-3">
                {tgtSearch ? (t('common.no_results') || 'Sin resultados') : (t('projects.bulk.all_paired') || 'Todas emparejadas')}
              </p>
            ) : filteredTgt.map(tbl => (
              <div key={tbl} className="text-xs px-2.5 py-1.5 rounded-md text-slate-700 truncate">
                {tbl}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Selected pairs */}
      {pairs.length > 0 && (
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
            {t('projects.bulk.selected_pairs') || 'Pares seleccionados'} ({pairs.length})
          </label>
          <div className="max-h-48 overflow-y-auto space-y-1.5">
            {pairs.map((pair, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-xs text-slate-400 font-mono w-5 shrink-0">{idx + 1}</span>
                <span className="text-xs font-semibold text-slate-700 truncate flex-1">{pair.sourceTable}</span>
                <ArrowRight className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                <span className="text-xs font-semibold text-slate-700 truncate flex-1">{pair.targetTable}</span>
                <button
                  onClick={() => removePair(idx)}
                  className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual pair input */}
      <ManualPairAdder
        sourceTables={sourceTables}
        targetTables={targetTables}
        usedSources={usedSources}
        usedTargets={usedTargets}
        onAdd={addPair}
        t={t}
      />
    </div>
  );
};

// ── Manual pair input ──────────────────────────────────────────────────
const ManualPairAdder = ({ sourceTables, targetTables, usedSources, usedTargets, onAdd, t }) => {
  const [src, setSrc] = useState('');
  const [tgt, setTgt] = useState('');

  const availSrc = sourceTables.filter(s => !usedSources.has(s));
  const availTgt = targetTables.filter(s => !usedTargets.has(s));

  const handleAdd = () => {
    if (src && tgt) {
      onAdd(src, tgt);
      setSrc('');
      setTgt('');
    }
  };

  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <label className="block text-[10px] font-bold text-slate-500 mb-1">
          {t('wizard.step_source.title') || 'Origen'}
        </label>
        <select
          value={src} onChange={e => setSrc(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="">{t('common.select') || 'Seleccionar...'}</option>
          {availSrc.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 mb-2 shrink-0" />
      <div className="flex-1">
        <label className="block text-[10px] font-bold text-slate-500 mb-1">
          {t('wizard.step_target.title') || 'Destino'}
        </label>
        <select
          value={tgt} onChange={e => setTgt(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="">{t('common.select') || 'Seleccionar...'}</option>
          {availTgt.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <button
        onClick={handleAdd}
        disabled={!src || !tgt}
        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold disabled:opacity-40 hover:bg-indigo-700 transition shrink-0 mb-0"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// STEP 3: PREVIEW
// ══════════════════════════════════════════════════════════════════════

const StepPreview = ({ previews, excludedIdxs, onToggleExclude, isLoading, t }) => {
  const [expanded, setExpanded] = useState(null);
  const activeCount = previews.filter((_, i) => !excludedIdxs.has(i) && !previews[i].error).length;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500 mt-3">
          {t('projects.bulk.analyzing') || 'Analizando columnas y ejecutando AI Match...'}
        </p>
      </div>
    );
  }

  if (!previews || previews.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">{t('common.no_data') || 'Sin datos.'}</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {t('projects.bulk.step_preview_desc') || 'Revisa el mapeo automático de columnas para cada par. Puedes ajustar el mapping después de crear los pipelines.'}
        </p>
        {excludedIdxs.size > 0 && (
          <span className="text-xs font-bold text-slate-500 shrink-0 ml-2">
            {activeCount}/{previews.length} {t('projects.bulk.active_pairs') || 'activos'}
          </span>
        )}
      </div>

      <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
        {previews.map((pv, idx) => {
          const coverage = pv.targetColumnCount > 0
            ? Math.round((pv.matchedColumns?.length || 0) / pv.targetColumnCount * 100)
            : 0;
          const level = getConfidenceLevel(coverage);
          const isExpanded = expanded === idx;
          const hasError = Boolean(pv.error);
          const isExcluded = excludedIdxs.has(idx);

          return (
            <div key={idx} className={`border rounded-xl overflow-hidden transition-all
              ${isExcluded ? 'border-slate-200 bg-slate-50 opacity-50' :
                hasError ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white'}`}>
              {/* Header */}
              <div className="flex items-center">
                <button
                  onClick={() => setExpanded(isExpanded ? null : idx)}
                  className={`flex-1 flex items-center gap-2 px-3 py-3 text-left hover:bg-slate-50 transition min-w-0
                    ${isExcluded ? 'line-through decoration-slate-400' : ''}`}
                >
                  <span className="text-[10px] text-slate-400 font-mono w-4 shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 text-xs sm:text-sm min-w-0">
                      <span title={pv.sourceTable} className={`font-semibold truncate max-w-[40%] ${isExcluded ? 'text-slate-400' : 'text-slate-800'}`}>{pv.sourceTable}</span>
                      <ArrowRight className={`w-3 h-3 shrink-0 ${isExcluded ? 'text-slate-300' : 'text-indigo-400'}`} />
                      <span title={pv.targetTable} className={`font-semibold truncate max-w-[40%] ${isExcluded ? 'text-slate-400' : 'text-slate-800'}`}>{pv.targetTable}</span>
                    </div>
                    {!isExcluded && (
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-[10px] text-slate-500">
                          {pv.matchedColumns?.length || 0}/{pv.targetColumnCount} {t('projects.bulk.columns_mapped') || 'mapeadas'}
                        </span>
                        {pv.warnings?.includes('NOT_NULL_UNMAPPED') && (
                          <span className="flex items-center gap-0.5 text-[10px] text-amber-600">
                            <AlertTriangle className="w-3 h-3" />
                            NOT NULL
                          </span>
                        )}
                      </div>
                    )}
                    {isExcluded && (
                      <span className="text-[10px] text-slate-400">{t('projects.bulk.excluded') || 'Excluido'}</span>
                    )}
                  </div>
                  {!isExcluded && (hasError ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded border bg-red-50 text-red-600 border-red-200">
                      Error
                    </span>
                  ) : (
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded border shrink-0 ${CONFIDENCE_COLORS[level]}`}
                      title={`${t('projects.bulk.coverage') || 'Cobertura'}: ${pv.matchedColumns?.length || 0}/${pv.targetColumnCount} · ${t('projects.bulk.avg_confidence') || 'Confianza promedio'}: ${pv.avgConfidence}%`}
                    >
                      {coverage}%
                    </span>
                  ))}
                  {!isExcluded && (isExpanded
                    ? <Minus className="w-4 h-4 text-slate-400 shrink-0" />
                    : <Plus className="w-4 h-4 text-slate-400 shrink-0" />
                  )}
                </button>
                {/* Botón excluir/incluir */}
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleExclude(idx); }}
                  title={isExcluded ? (t('projects.bulk.include') || 'Incluir') : (t('projects.bulk.exclude') || 'Excluir')}
                  className={`mx-2 p-1.5 rounded-lg transition shrink-0
                    ${isExcluded
                      ? 'text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700'
                      : 'text-slate-400 hover:bg-red-50 hover:text-red-500'
                    }`}
                >
                  {isExcluded ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="px-4 pb-3 border-t border-slate-100">
                  {hasError ? (
                    <p className="text-xs text-red-600 py-2">{pv.error}</p>
                  ) : (
                    <div className="space-y-2 pt-2">
                      {pv.matchedColumns?.map((col, ci) => (
                        <div key={ci} className="flex items-center gap-2 text-xs">
                          <span className="text-slate-600 truncate flex-1">{col.source}</span>
                          <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="text-slate-600 truncate flex-1">{col.target}</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border shrink-0
                            ${CONFIDENCE_COLORS[getConfidenceLevel(col.confidence)]}`}>
                            {col.confidence}%
                          </span>
                        </div>
                      ))}
                      {pv.unmatchedTargetColumns?.length > 0 && (
                        <div className="pt-1">
                          <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">
                            {t('projects.bulk.unmapped') || 'Sin mapear'}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {pv.unmatchedTargetColumns.map(c => (
                              <span key={c} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded">
                                {c}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// STEP 4: CONFIRM
// ══════════════════════════════════════════════════════════════════════

const StepConfirm = ({ pairs, previews, excludedIdxs, phases, selectedPhase, onSelectPhase, syncMode, onSyncModeChange, copySchedule, onCopyScheduleChange, refPipeline, isCreating, t }) => {
  const activePreviews = (previews || []).filter((_, i) => !excludedIdxs.has(i));
  const totalMatched = activePreviews.reduce((sum, p) => sum + (p.matchedColumns?.length || 0), 0);
  const totalCols = activePreviews.reduce((sum, p) => sum + (p.targetColumnCount || 0), 0);
  const errorCount = activePreviews.filter(p => p.error).length;

  // ── Análisis de warnings ──
  const pipelinesWithWarnings = activePreviews.filter(p =>
    !p.error && (
      (p.warnings && p.warnings.length > 0)
      || (p.unmatchedTargetColumns && p.unmatchedTargetColumns.length > 0)
      || (p.avgConfidence > 0 && p.avgConfidence < 70)
    )
  );
  const notNullCount = activePreviews.filter(p =>
    p.warnings?.includes('NOT_NULL_UNMAPPED')
  ).length;
  const lowConfidenceCount = activePreviews.filter(p =>
    !p.error && p.avgConfidence > 0 && p.avgConfidence < 70
  ).length;
  const unmappedCount = activePreviews.filter(p =>
    !p.error && p.unmatchedTargetColumns?.length > 0
  ).length;
  const hasIssues = pipelinesWithWarnings.length > 0;

  return (
    <div className="space-y-5">
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
          <div className="text-sm text-indigo-800 space-y-1">
            <p className="font-semibold">
              {t('projects.bulk.confirm_summary') || 'Se crearán'} {activePreviews.length - errorCount} {t('projects.bulk.pipelines_label') || 'pipelines'}
            </p>
            <p className="text-indigo-600 text-xs">
              {totalMatched}/{totalCols} {t('projects.bulk.columns_auto_mapped') || 'columnas mapeadas automáticamente'}
              {errorCount > 0 && ` · ${errorCount} ${t('projects.bulk.with_errors') || 'con errores (se omitirán)'}`}
            </p>
          </div>
        </div>
      </div>

      {/* ── Warning banner — pipelines que requieren revisión ── */}
      {hasIssues && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-sm font-semibold text-amber-800">
                {t('projects.bulk.review_warning_title') || 'Revisión recomendada antes de ejecutar'}
              </p>
              <div className="text-xs text-amber-700 space-y-2">
                {notNullCount > 0 && (
                  <div>
                    <p className="font-medium">
                      • {notNullCount} {notNullCount === 1 ? 'pipeline tiene' : 'pipelines tienen'}{' '}
                      {t('projects.bulk.warn_not_null') || 'columnas obligatorias (NOT NULL) sin mapear:'}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1 ml-3">
                      {activePreviews.filter(p => p.warnings?.includes('NOT_NULL_UNMAPPED')).map(p => (
                        <span key={p.targetTable} className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                          {p.targetTable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {lowConfidenceCount > 0 && (
                  <div>
                    <p className="font-medium">
                      • {lowConfidenceCount} {lowConfidenceCount === 1 ? 'pipeline tiene' : 'pipelines tienen'}{' '}
                      {t('projects.bulk.warn_low_confidence') || 'confianza de mapeo baja (< 70%):'}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1 ml-3">
                      {activePreviews.filter(p => !p.error && p.avgConfidence > 0 && p.avgConfidence < 70).map(p => (
                        <span key={p.targetTable} className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                          {p.targetTable} ({p.avgConfidence}%)
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {unmappedCount > 0 && notNullCount === 0 && (
                  <div>
                    <p className="font-medium">
                      • {unmappedCount} {unmappedCount === 1 ? 'pipeline tiene' : 'pipelines tienen'}{' '}
                      {t('projects.bulk.warn_unmapped') || 'columnas destino sin mapear:'}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1 ml-3">
                      {activePreviews.filter(p => !p.error && p.unmatchedTargetColumns?.length > 0).map(p => (
                        <span key={p.targetTable} className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded">
                          {p.targetTable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-amber-600 font-medium">
                {t('projects.bulk.review_warning_action') || 'Después de crear, abre cada pipeline desde el dashboard para revisar y ajustar el mapping antes de ejecutar el proyecto.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Mode */}
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1.5">
          {t('wizard.step_target.sync_strategy') || 'Estrategia de Sincronización'}
        </label>
        <select
          value={syncMode} onChange={e => onSyncModeChange(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="upsert">Upsert</option>
          <option value="append">Append</option>
          <option value="overwrite">Overwrite</option>
        </select>
      </div>

      {/* Phase assignment */}
      <div>
        <label className="block text-xs font-bold text-slate-600 mb-1.5">
          {t('projects.bulk.assign_phase') || 'Asignar a fase (opcional)'}
        </label>
        <select
          value={selectedPhase || ''} onChange={e => onSelectPhase(e.target.value ? parseInt(e.target.value) : null)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="">{t('projects.bulk.no_phase') || 'Sin asignar a fase'}</option>
          {phases.map(ph => (
            <option key={ph.idProjectPhase} value={ph.idProjectPhase}>
              {ph.phaseOrder}. {ph.name}
            </option>
          ))}
        </select>
      </div>

      {/* Copiar programación */}
      <label className="flex items-start gap-3 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl p-3">
        <input
          type="checkbox"
          checked={copySchedule}
          onChange={e => onCopyScheduleChange(e.target.checked)}
          className="mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <div>
          <span className="text-xs font-bold text-slate-700">
            {t('projects.bulk.copy_schedule') || 'Copiar programación del pipeline de referencia'}
          </span>
          {refPipeline && (
            <p className="text-[10px] text-slate-500 mt-0.5">
              {refPipeline.frequencyType === 'once'
                ? (t('projects.bulk.schedule_once') || 'Ejecución única (manual)')
                : `${refPipeline.frequencyType} · ${refPipeline.cronExpression || ''}`
              }
            </p>
          )}
        </div>
      </label>

      <p className="text-xs text-slate-500">
        {t('projects.bulk.post_create_note') || 'Después de crear los pipelines podrás ajustar el mapping de columnas, agregar transformaciones y configurar reglas de calidad desde el wizard individual de cada pipeline.'}
      </p>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// STEP 5: SUCCESS
// ══════════════════════════════════════════════════════════════════════

const StepSuccess = ({ createdCount, hasWarnings, onClose, t }) => (
  <div className="space-y-5 text-center py-4">
    <div className="flex justify-center">
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
    </div>
    <div>
      <p className="text-lg font-black text-slate-900">
        {createdCount} {createdCount === 1 ? 'pipeline creado' : 'pipelines creados'}
      </p>
      <p className="text-sm text-slate-500 mt-1">
        {t('projects.bulk.success_desc') || 'Los pipelines fueron creados y asignados al proyecto.'}
      </p>
    </div>

    {hasWarnings && (
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-left">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {t('projects.bulk.success_review_title') || 'No olvides revisar los pipelines'}
            </p>
            <p className="text-xs text-amber-700 mt-1">
              {t('projects.bulk.success_review_desc') || 'Algunos pipelines tienen columnas sin mapear o con baja confianza. Abre cada uno desde la fase para revisar el mapping antes de ejecutar el proyecto.'}
            </p>
          </div>
        </div>
      </div>
    )}

    <button
      onClick={onClose}
      className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-sm transition"
    >
      {t('common.close') || 'Cerrar'}
    </button>
  </div>
);

// ══════════════════════════════════════════════════════════════════════
// MAIN WIZARD COMPONENT
// ══════════════════════════════════════════════════════════════════════

export const BulkPipelineWizard = ({ isOpen, idProject, phases, onClose, onComplete }) => {
  const { t } = useLanguageStore();
  const {
    allPipelines, fetchPipelinesForPicker,
    fetchBulkAvailableTables, bulkAvailableTables,
    fetchBulkPreview, bulkPreviews,
    createBulkPipelines, clearBulkState,
    isLoadingBulk, isSaving,
  } = useProjectStore();

  const [step, setStep] = useState(0);
  const [refPipelineId, setRefPipelineId] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [syncMode, setSyncMode] = useState('upsert');
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [copySchedule, setCopySchedule] = useState(false);
  const [error, setError] = useState('');
  const [createdResult, setCreatedResult] = useState(null); // { created, hasWarnings }
  const [excludedIdxs, setExcludedIdxs] = useState(new Set()); // índices excluidos del preview

  // Pipelines del proyecto
  const projectPipelines = useMemo(() =>
    allPipelines.filter(p => p.idProject === parseInt(idProject) && p.status === 1),
    [allPipelines, idProject]
  );

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setRefPipelineId(null);
      setPairs([]);
      setSyncMode('upsert');
      setSelectedPhase(null);
      setCopySchedule(false);
      setError('');
      setCreatedResult(null);
      setExcludedIdxs(new Set());
      clearBulkState();
      fetchPipelinesForPicker();
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ── Step navigation ──
  const canNext = useMemo(() => {
    if (step === 0) return refPipelineId !== null;
    if (step === 1) return pairs.length > 0;
    if (step === 2) return bulkPreviews && bulkPreviews.previews?.length > 0;
    if (step === 3) return !isSaving;
    return false;
  }, [step, refPipelineId, pairs, bulkPreviews, isSaving]);

  const handleNext = async () => {
    setError('');

    if (step === 0) {
      // Moving to step 1 → fetch available tables
      const result = await fetchBulkAvailableTables(idProject, refPipelineId);
      if (result.ok) {
        setStep(1);
      } else {
        const detail = result.error;
        setError(
          typeof detail === 'object' && detail?.code
            ? t(`errors.${detail.code}`, detail.params || {}) || detail.fallback || detail.code
            : String(detail || t('common.error') || 'Error')
        );
      }
      return;
    }

    if (step === 1) {
      // Moving to step 2 → run preview
      const result = await fetchBulkPreview(idProject, {
        referenceConfigHeadId: refPipelineId,
        pairs,
        syncMode,
      });
      if (result.ok) {
        setStep(2);
      } else {
        const detail = result.error;
        setError(
          typeof detail === 'object' && detail?.code
            ? t(`errors.${detail.code}`, detail.params || {}) || detail.fallback || detail.code
            : String(detail || t('common.error') || 'Error')
        );
      }
      return;
    }

    if (step === 2) {
      setStep(3);
      return;
    }

    if (step === 3) {
      // Create pipelines
      const validPairs = (bulkPreviews?.previews || [])
        .filter((p, i) => !p.error && !excludedIdxs.has(i))
        .map(p => ({
          sourceTable: p.sourceTable,
          targetTable: p.targetTable,
          syncMode,
          idProjectPhase: selectedPhase || null,
          phaseOrder: selectedPhase
            ? (allPipelines.filter(pl => pl.idProjectPhase === selectedPhase).length + 1)
            : null,
        }));

      if (validPairs.length === 0) {
        setError(t('projects.bulk.no_valid_pairs') || 'No hay pares válidos para crear.');
        return;
      }

      const result = await createBulkPipelines(idProject, {
        referenceConfigHeadId: refPipelineId,
        pipelines: validPairs,
        copySchedule,
      });

      if (result.ok) {
        // Detectar si hay warnings para mostrar en el paso de éxito
        const hasWarnings = (bulkPreviews?.previews || []).some((p, i) =>
          !p.error && !excludedIdxs.has(i) && (
            (p.warnings && p.warnings.length > 0)
            || (p.unmatchedTargetColumns && p.unmatchedTargetColumns.length > 0)
            || (p.avgConfidence > 0 && p.avgConfidence < 70)
          )
        );
        setCreatedResult({ created: result.data.created, hasWarnings });
        onComplete(result.data);  // Recargar datos en ProjectDetail
        setStep(4);               // Mostrar paso de éxito
      } else {
        const detail = result.error;
        setError(
          typeof detail === 'object' && detail?.code
            ? t(`errors.${detail.code}`, detail.params || {}) || detail.fallback || detail.code
            : String(detail || t('common.error') || 'Error')
        );
      }
    }
  };

  const handleBack = () => {
    setError('');
    if (step > 0) setStep(step - 1);
  };

  if (!isOpen) return null;

  const stepTitles = [
    t('projects.bulk.step_reference') || 'Pipeline de referencia',
    t('projects.bulk.step_pairs') || 'Seleccionar tablas',
    t('projects.bulk.step_preview') || 'Preview AI Match',
    t('projects.bulk.step_confirm') || 'Confirmar creación',
  ];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-2 sm:mx-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-indigo-600" />
              {t('projects.bulk.title') || 'Creación masiva de pipelines'}
            </h2>
            {step < 4 && (
              <p className="text-xs text-slate-500 mt-0.5">
                {t('projects.bulk.step_label') || 'Paso'} {step + 1}/4: {stepTitles[step]}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator — oculto en paso de éxito */}
        {step < 4 && (
          <div className="flex gap-1 px-6 pt-3 shrink-0">
            {STEPS.slice(0, 4).map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors
                ${i <= step ? 'bg-indigo-500' : 'bg-slate-200'}`}
              />
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === 0 && (
            <StepReference
              pipelines={projectPipelines}
              selected={refPipelineId}
              onSelect={setRefPipelineId}
              t={t}
            />
          )}
          {step === 1 && (
            <StepPairs
              sourceTables={bulkAvailableTables?.sourceTables || []}
              targetTables={bulkAvailableTables?.targetTables || []}
              sourceEngine={bulkAvailableTables?.sourceEngine || ''}
              targetEngine={bulkAvailableTables?.targetEngine || ''}
              pairs={pairs}
              setPairs={setPairs}
              isLoading={isLoadingBulk}
              t={t}
            />
          )}
          {step === 2 && (
            <StepPreview
              previews={bulkPreviews?.previews || []}
              excludedIdxs={excludedIdxs}
              onToggleExclude={(idx) => setExcludedIdxs(prev => {
                const next = new Set(prev);
                next.has(idx) ? next.delete(idx) : next.add(idx);
                return next;
              })}
              isLoading={isLoadingBulk}
              t={t}
            />
          )}
          {step === 3 && (
            <StepConfirm
              pairs={pairs}
              previews={bulkPreviews?.previews || []}
              excludedIdxs={excludedIdxs}
              phases={phases || []}
              selectedPhase={selectedPhase}
              onSelectPhase={setSelectedPhase}
              syncMode={syncMode}
              onSyncModeChange={setSyncMode}
              copySchedule={copySchedule}
              onCopyScheduleChange={setCopySchedule}
              refPipeline={projectPipelines.find(p => p.id === refPipelineId)}
              isCreating={isSaving}
              t={t}
            />
          )}
          {step === 4 && createdResult && (
            <StepSuccess
              createdCount={createdResult.created}
              hasWarnings={createdResult.hasWarnings}
              onClose={onClose}
              t={t}
            />
          )}
        </div>

        {/* Error */}
        {error && step < 4 && (
          <div className="mx-6 mb-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Footer — oculto en paso de éxito (tiene su propio botón) */}
        {step < 4 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 shrink-0">
            <button
              onClick={step === 0 ? onClose : handleBack}
              disabled={isLoadingBulk || isSaving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
              {step === 0 ? (t('common.cancel') || 'Cancelar') : (t('common.back') || 'Atrás')}
            </button>

            <button
              onClick={handleNext}
              disabled={!canNext || isLoadingBulk || isSaving}
              className="flex items-center gap-1.5 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-sm transition disabled:opacity-50"
            >
              {(isLoadingBulk || isSaving) && <Loader2 className="w-4 h-4 animate-spin" />}
              {step === 3
                ? (isSaving
                  ? (t('common.creating') || 'Creando...')
                  : (t('projects.bulk.create_all') || 'Crear pipelines'))
                : (t('common.next') || 'Siguiente')
              }
              {step < 3 && !isLoadingBulk && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkPipelineWizard;
